/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo } from 'react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { PlaceholderText } from '../ui/Placeholder';
import { TabContainer, Tab } from '../ui';
import type { Product } from '@smartphone-tycoon/shared-core';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useFinancialHistoryStore } from '../../stores/financialHistoryStore';
import { useCompanyStore, selectTotalEmployeeCost } from '../../stores/companyStore';
import { useBuildingStore } from '../../stores/buildingStore';
import { buildingService } from '../../services/tycoon/buildingService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface FinanceViewProps {
  companyState: {
    cash: number;
    day: number;
    month: number;
    year: number;
    dailyStats: {
      revenue: number;
      costs: number;
      profit: number;
    };
  };
  products: Product[];
}


export function FinanceView({ companyState }: FinanceViewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'cashflow' | 'projections'>('overview');

  // Calculate monthly totals based on daily stats
  const monthlyRevenue = companyState.dailyStats.revenue * 30;
  const monthlyCosts = companyState.dailyStats.costs * 30;
  const monthlyProfit = companyState.dailyStats.profit * 30;

  // Get historical financial data from the store
  const monthlySnapshots = useFinancialHistoryStore(state => state.monthlySnapshots);

  // Get real cost breakdown data
  const dailySalaryCost = useCompanyStore(selectTotalEmployeeCost);
  const monthlySalaryCost = dailySalaryCost * 30;
  const monthlyRDInvestment = useCompanyStore(state => state.research.monthlyInvestment);
  const buildings = useBuildingStore(state => state.buildings);
  const buildingCosts = buildingService.calculateMaintenanceCosts(buildings || []);
  const monthlyBuildingMaintenance = buildingCosts.totalMaintenance;

  // Build 12-month chart data from real historical snapshots
  const historicalData = useMemo(() => {
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data: { month: string; revenue: number; costs: number; profit: number; cash: number | null }[] = [];

    // Generate labels for last 12 months (oldest to newest)
    for (let i = 11; i >= 0; i--) {
      let targetMonth = companyState.month - i;
      let targetYear = companyState.year;

      // Handle year wraparound
      while (targetMonth <= 0) {
        targetMonth += 12;
        targetYear -= 1;
      }

      // Find matching snapshot
      const snapshot = monthlySnapshots.find(
        s => s.date.month === targetMonth && s.date.year === targetYear
      );

      data.push({
        month: monthLabels[targetMonth - 1],
        revenue: snapshot?.revenue ?? 0,
        costs: snapshot?.costs ?? 0,
        profit: snapshot?.profit ?? 0,
        cash: snapshot?.cash ?? null
      });
    }

    return data;
  }, [monthlySnapshots, companyState.month, companyState.year]);

  const chartData = useMemo(() => ({
    labels: historicalData.map(d => d.month),
    datasets: [
      {
        label: 'Revenue',
        data: historicalData.map(d => d.revenue),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        tension: 0.4
      },
      {
        label: 'Costs',
        data: historicalData.map(d => d.costs),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.5)',
        tension: 0.4
      },
      {
        label: 'Profit',
        data: historicalData.map(d => d.profit),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        tension: 0.4
      },
      {
        label: 'Cash Balance',
        data: historicalData.map(d => d.cash),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.5)',
        tension: 0.4,
        borderDash: [5, 5]
      }
    ]
  }), [historicalData]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += formatCurrency(Math.round(context.parsed.y));
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value: number) => formatCurrency(Math.round(value))
        }
      }
    }
  }), []);

  // Calculate P&L statement with real data
  // Operations = salaries + building maintenance
  const operationsCost = monthlySalaryCost + monthlyBuildingMaintenance;

  // COGS = total costs minus known categories (salaries, maintenance, R&D)
  // This captures production/manufacturing costs
  const rawCogs = monthlyCosts - operationsCost - monthlyRDInvestment;
  if (import.meta.env.DEV && rawCogs < 0) {
    console.warn('[FinanceView] COGS clamped to 0, raw value:', rawCogs, {
      monthlyCosts,
      operationsCost,
      monthlyRDInvestment
    });
  }
  const cogsCost = Math.max(0, rawCogs);

  const plStatement = {
    revenue: {
      productSales: monthlyRevenue,
      total: monthlyRevenue
    },
    expenses: {
      cogs: cogsCost,
      rd: monthlyRDInvestment,
      operations: operationsCost,
      total: monthlyCosts
    },
    grossProfit: monthlyRevenue - cogsCost,
    operatingProfit: monthlyProfit,
    netProfit: monthlyProfit
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'cashflow', label: 'Cash Flow' },
    { id: 'projections', label: 'Projections' },
  ];

  return (
    <TabContainer tabs={tabs} activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}>
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-theme-bg-content rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Profit & Loss Statement</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-theme-text-secondary mb-2">Revenue</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-theme-text-secondary">Product Sales</span>
                      <span className="font-medium">{formatCurrency(plStatement.revenue.productSales)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium pt-2 border-t">
                      <span>Total Revenue</span>
                      <span className="text-theme-success">{formatCurrency(plStatement.revenue.total)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-theme-text-secondary mb-2">Expenses</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-theme-text-secondary">Cost of Goods Sold</span>
                      <span className="font-medium text-theme-error">-{formatCurrency(plStatement.expenses.cogs)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-theme-text-secondary">R&D</span>
                      <span className="font-medium text-theme-error">-{formatCurrency(plStatement.expenses.rd)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-theme-text-secondary">Operations</span>
                      <span className="font-medium text-theme-error">-{formatCurrency(plStatement.expenses.operations)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium pt-2 border-t">
                      <span>Total Expenses</span>
                      <span className="text-theme-error">-{formatCurrency(plStatement.expenses.total)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Gross Profit</span>
                    <span className={`font-medium ${plStatement.grossProfit >= 0 ? 'text-theme-success' : 'text-theme-error'}`}>
                      {formatCurrency(plStatement.grossProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Operating Profit</span>
                    <span className={`font-medium ${plStatement.operatingProfit >= 0 ? 'text-theme-success' : 'text-theme-error'}`}>
                      {formatCurrency(plStatement.operatingProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-medium pt-2 border-t">
                    <span>Net Profit</span>
                    <span className={`${plStatement.netProfit >= 0 ? 'text-theme-success' : 'text-theme-error'}`}>
                      {formatCurrency(plStatement.netProfit)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-theme-bg-content rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Financial Overview</h3>
              <div className="h-64">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cashflow' && (
          <div className="bg-theme-bg-content rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Cash Flow Statement</h3>
            <div className="text-center py-8">
              <PlaceholderText text="Cash flow tracking and analysis coming soon." />
            </div>
          </div>
        )}

        {activeTab === 'projections' && (
          <div className="bg-theme-bg-content rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Financial Projections</h3>
            <div className="text-center py-8">
              <PlaceholderText text="Revenue and expense projections coming soon." />
            </div>
          </div>
        )}
      </div>
    </TabContainer>
  );
}
